<template>
  <div class="card modal-overflow add-job" style="box-shadow: none;">
    <h5 style="text-align: center; padding-top: 12px;">{{ projectAddress }}</h5>
    <div class="card-body new-job" style="padding-top: 0;">
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label>Job Name</label>
            <div class="job-name-container grey-font">
              <h6>{{ create.name }}</h6>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>
              Job Number
              <el-tooltip effect="dark" content="Click the ' + ' button to auto-generate a job number with the pattern (yyyy-mm--xxx). Alternatively, you can enter any value you choose. The job number will be appended to the customer name when generating an invoice in QuickBooks. This will associate the invoice with the specified QuickBooks job." placement="top">
                <img class="icon-help" src="static/img/icon-help.png"/>
              </el-tooltip>
            </label>
            <button @click="generateJobNumber" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> + </button>
            <el-input
              size="large"
              name="jobNumber"
              clearable
              :disabled="!canEdit"
              class="job-number"
              v-model="create.number"
              @blur="ifJobNumberExistsThenWarn"
              placeholder="Job Number">
            </el-input>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>
              Permit Number
            </label>
            <el-input type="input"
                      name="permitNumber"
                      placeholder="Permit Number"
                      v-model="create.permitNumber"
                      clearable
                      :disabled="!canEdit" />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-3">
          <div class="form-group">
            <label>Job Coordinator</label>
            <el-autocomplete
              style="width:100%"
              data-vv-as="job coordinator"
              size="large"
              name="coordinatorId"
              class="inline-input"
              clearable
              :disabled="!canEdit"
              v-validate="modelValidations.coordinatorId"
              v-model="selectedCoordinator"
              :fetch-suggestions="findCoordinators"
              :trigger-on-focus="false"
              value-key="name"
              placeholder="Job Coordinator"
              @blur="verifyCoordinatorAutocompleteSelected"
              @select="setCoordinator">
            </el-autocomplete>
            <small class="text-danger" v-show="errors.has('coordinatorId')">
              {{ getError('coordinatorId') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Job Priority</label>
            <el-select class="select"
                       name="priority"
                       size="large"
                       clearable
                       :disabled="!canEdit"
                       placeholder="Job Priority"
                       v-validate="modelValidations.priority"
                       v-model="create.priority">
              <el-option v-for="option in priorities"
                         class="select"
                         :value="option.value"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
            <small class="text-danger" v-show="errors.has('priority')">
              {{ getError('priority') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Job Status</label>
            <el-select class="select"
                       name="status"
                       size="large"
                       clearable
                       :disabled="!canEdit"
                       placeholder="Job Status"
                       v-validate="modelValidations.status"
                       v-model="create.status">
              <el-option v-for="option in statuses"
                         class="select"
                         :value="option.value"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
            <small class="text-danger" v-show="errors.has('status')">
              {{ getError('status') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Elevation</label>
            <el-input type="input"
                      name="elevation"
                      placeholder="Elevation"
                      v-model="create.elevation"
                      clearable
                      :disabled="!canEdit" />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-3">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.client" @change="toggleRememberedClient">Billable Client</el-checkbox>
            <button v-if="isClientResult && canEdit" @click="copyClientToBuilder" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> > </button>
            <div v-if="hasAdminNotes" style="display: inline;">
              <el-tooltip effect="dark" placement="top" style="padding: 1px 5px; margin-top: -1px; float: right;">
                <div slot="content">
                  <h6 style="text-align: center">Notes</h6>
                  <p v-html="adminNotes"></p>
                </div>
                <img class="icon-help m-left-4" src="static/img/icon-info.png" />
              </el-tooltip>
            </div>
            <div v-if="hasTravelCharge" style="display: inline;">
              <el-tooltip effect="dark" placement="top" style="padding: 1px 0; margin-top: -1px; float: right;">
                <div slot="content">
                  <h6 style="text-align: center">Travel Charge</h6>
                  <p style="text-align: center; margin-bottom: 0">{{currentFormatter.format(travelCharge.value)}}</p>
                  <span style="margin-top: 5px;" v-if="isMultiplePriceRules">Multiple price rules matched, actual travel charge may be zero.</span>
              </div>
              <i class="nc-icon nc-money-coins" style="margin-right: 3px;"></i>
              </el-tooltip>
            </div>
            <el-autocomplete
              ref="clientId"
              style="width:100%"
              name="clientId"
              class="inline-input"
              :debounce="100"
              clearable
              :disabled="!canEdit"
              v-model="selectedClient.name"
              :fetch-suggestions="findClients"
              :trigger-on-focus="false"
              value-key="name"
              placeholder="Billable Client"
              @blur="verifyClientAutocompleteSelected"
              @select="setClient">
            </el-autocomplete>
            <small class="text-danger" v-show="errors.has('clientId')">
              {{ getError('clientId') }}
            </small>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('clientId')">
              {{ fieldNotValidError('Client') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.clientContact" @change="toggleRememberedClientContact">Client Contact</el-checkbox>
            <button v-if="isClientResult && canEdit" @click="addClientContact" style="margin-bottom: 0;" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> + </button>
            <button v-if="isClientResult && create.clientContactId && canEdit" @click="editClientContact" style="margin-bottom: 0;" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> edit </button>
            <v-select
                v-model="selectedClientContact"
                @input="setClientContactMaybeRemember"
                placeholder="Client Contact"
                :disabled="!canEdit"
                label="label"
                value="value"
                key="label"
                :options="clientContacts">
            </v-select>
            <small class="text-danger" v-show="errors.has('clientContactId')">
              {{ getError('clientContactId') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.builder" @change="toggleRememberedBuilder">Builder</el-checkbox>
            <button v-if="isBuilderResult && canEdit" @click="copyBuilderToClient" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> < </button>
            <el-autocomplete
              ref="builderId"
              style="width:100%"
              name="builderId"
              class="inline-input"
              :debounce="150"
              clearable
              :disabled="!canEdit"
              v-model="selectedBuilder.name"
              :fetch-suggestions="findBuilders"
              :trigger-on-focus="false"
              value-key="name"
              placeholder="Builder"
              @blur="verifyBuilderAutocompleteSelected"
              @select="setBuilder">
            </el-autocomplete>
            <small class="text-danger" v-show="errors.has('builderId')">
              {{ getError('builderId') }}
            </small>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('builderId')">
              {{ fieldNotValidError('Builder') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.builderContact" @change="toggleRememberedBuilderContact">Builder Contact</el-checkbox>
            <button v-if="isBuilderResult && canEdit" @click="addBuilderContact" style="margin-bottom: 0;" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> + </button>
            <button v-if="isBuilderResult && create.builderContactId && canEdit" @click="editBuilderContact" style="margin-bottom: 0;" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> edit </button>
            <v-select
                v-model="selectedBuilderContact"
                @input="setBuilderContactMaybeRemember"
                placeholder="Builder Contact"
                :disabled="!canEdit"
                label="label"
                value="value"
                key="label"
                :options="builderContacts">
            </v-select>
            <small class="text-danger" v-show="errors.has('builderContactId')">
              {{ getError('builderContactId') }}
            </small>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-3">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.community" @change="toggleRememberedCommunity">Community</el-checkbox>
            <el-autocomplete
              style="width:100%"
              ref="community"
              name="community"
              class="inline-input"
              clearable
              :disabled="!canEdit"
              v-model="selectedCommunity.name"
              :fetch-suggestions="findCommunities"
              :trigger-on-focus="false"
              value-key="name"
              placeholder="Community"
              @blur="verifyCommunityAutocompleteSelected"
              @focus="verifyClientOrBuilderSelected"
              @select="setCommunity">
            </el-autocomplete>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('community')">
              {{ fieldNotValidError('Community') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Plan Name</label>
            <span v-if="selectedPlanName && planNamePDF">
              <el-tooltip class="item" effect="dark" placement="right" style="padding: 1px 5px; margin-top: -2px; float: right;">
                <div slot="content" style="text-align: center"> {{ planNamePDF }} </div>
                <img src="static/img/file-mgr/pdf-small.png"/>
              </el-tooltip>
            </span>
            <button v-if="selectedPlanName && canEdit" @click="editPlanName" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> edit </button>
            <button v-if="!selectedPlanName && canEdit" @click="addPlanName" style="margin-bottom: 0;" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> + </button>
            <el-autocomplete
              style="width:100%"
              size="large"
              name="planNameId"
              ref="planNameId"
              class="inline-input"
              clearable
              v-model="selectedPlanName"
              :fetch-suggestions="findPlanNames"
              :trigger-on-focus="false"
              value-key="plan"
              placeholder="Plan Name"
              @blur="verifyPlanNameAutocompleteSelected"
              @focus="verifyBuilderSelected"
              @select="setPlanNameMaybeFetchPDF">
            </el-autocomplete>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('planNameId')">
              {{ fieldNotValidError('Plan Name') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Date Received</label>
            <el-date-picker
              :disabled="!canEdit"
              name="dateReceived"
              data-vv-as="data received"
              :format="constants.DATETIME.END_OF_DAY.DISPLAY"
              :value-format="constants.DATETIME.END_OF_DAY.VALUE"
              v-validate="modelValidations.dateReceived"
              v-model="dateReceived"
              type="date"
              :picker-options="datePickerConfig"
              @change="setDateReceived"
              placeholder="Pick a day">
            </el-date-picker>
            <small class="text-danger" v-show="errors.has('dateReceived')">
              {{ getError('dateReceived') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.targetCompletionDate" @change="toggleRememberedTargetCompletionDate">Target Completion Date</el-checkbox>
            <el-date-picker
              :disabled="!canEdit"
              name="targetCompletionDate"
              data-vv-as="target completion date"
              :format="constants.DATETIME.END_OF_DAY.DISPLAY"
              :value-format="constants.DATETIME.END_OF_DAY.VALUE"
              v-validate="modelValidations.targetCompletionDate"
              v-model="targetCompletionDate"
              type="date"
              :picker-options="datePickerConfig"
              @change="setTargetCompletionDate"
              placeholder="Pick a day">
            </el-date-picker>
            <small class="text-danger" v-show="errors.has('targetCompletionDate')">
              {{ getError('targetCompletionDate') }}
            </small>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <label>Description</label>
            <ckeditor :editor="editor" :disabled="!canEdit" v-model="localDescription" :config="ckEditorConfig"></ckeditor>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Constants from 'src/utilities/constants'
import Permissions from 'src/utilities/permissions'
import {
  autocompleteTextButNoValue,
  autocompleteTextButNoValueMessage,
  dateNoon,
  dateReceived,
  findCommunities,
  findCompany,
  nextBusinessDay,
  notifyError,
  notifySuccess,
  notifyWarning,
  objectBy,
  verifyAutocompleteSelected
} from 'src/utilities/utils'
import {mapGetters} from 'vuex'
import PSwitch from 'src/components/UIComponents/Switch.vue'
import CKEditor from '@ckeditor/ckeditor5-vue'
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor'
import ckEditorConfig from 'src/utilities/ckeditor'
import datePickerConfig from 'src/components/UIComponents/DatePicker/datePickerConfig'
import _ from 'lodash'
import {sync} from 'vuex-pathify'
import {
  communityExistsButNotAssociated,
  jobAddBuilderContact,
  jobAddClientContact,
  jobAddPlanName,
  jobCopyBuilderToClient,
  jobCopyClientToBuilder,
  jobEditBuilderContact,
  jobEditClientContact,
  jobEditPlanName,
  jobFetchPlanNamePDF,
  jobFetchTravelCharge,
  jobFindBuilders,
  jobFindClients,
  jobFindCoordinators,
  jobFindPlanNames,
  jobGenerateJobNumber,
  jobIfJobNumberExistsThenWarn,
  jobMaybeLoadBuilder,
  jobMaybeLoadClient,
  jobMaybeLoadCoordinator,
  jobNoCompanySelected,
  jobSetBuilder,
  jobSetClient,
  jobSetCommunity,
  jobSetCompany,
  jobSetCoordinator,
  jobSetPlanName,
  jobVerifyBuilderSelected,
  jobVerifyClientOrBuilderSelected
} from 'src/utilities/jobHelper'
import vSelect from 'vue-select'
import {OK} from 'http-status-codes'

const CREATE = 'create'

  export default {
    props: ['data'],
    components: {
      PSwitch,
      ckeditor: CKEditor.component,
      vSelect
    },
    computed: {
      create: sync('jobs@create'),
      remember: sync('jobs@remember'),
      ...mapGetters({
        priorities: 'priorities/job',
        contacts: 'companies/companyContacts',
        activeModals: 'modalTracker/activeModals',
        autocompleteClients: 'companies/autocompleteResultsClient',
        autocompleteBuilders: 'companies/autocompleteResultsBuilder'
      }),
      statuses() {
        const allStatuses = this.$store.getters['statuses/job']
        return _.filter(allStatuses, (status) => status.value !== Constants.STATUSES.CANCELLED && status.value !== Constants.STATUSES.COMPLETED)
      },
      canRead() {
        return this.$can('read', Permissions.JOBS)
      },
      canEdit() {
        return this.$can('edit', Permissions.JOBS)
      },
      hasTravelCharge() {
        return this.travelCharge.value !== null
      },
      isMultiplePriceRules() {
        return this.travelCharge.state === Constants.TRAVEL_CHARGE.STATES.UNDECIDED
      },
      hasAdminNotes() {
        return typeof(this.adminNotes) === 'string' && this.adminNotes.trim()
      },
      projectAddress() {
        const details = this.$store.get('projects/edit/details/all')
        const address = this.$store.get('projects/edit/address/all')
        if (address.streetNumber && address.streetName && address.city) {
          return `${details.projectType} Project: ${address.streetNumber || ''} ${address.streetName || ''}, ${address.city}, ${address.state}`
        }
        return ''
      },
    },
    data() {
      return {
        editor: ClassicEditor,
        ckEditorConfig,
        datePickerConfig,
        allDepartmentJobs: null,
        localDescription: '',
        selectedPlanName: null,
        planNamePDF: null,
        selectedCoordinator: null,
        selectedCommunity: { id: null, name: null },
        selectedClient: { id: null, name: null },
        selectedBuilder: { id: null, name: null },
        selectedClientContact: null,
        selectedBuilderContact: null,
        dateReceived: dateReceived(),
        targetCompletionDate: nextBusinessDay(),
        isClientResult: false,
        isBuilderResult: false,
        clientContacts: [],
        builderContacts: [],
        isAddClientContact: false,
        isAddBuilderContact: false,
        onHoldWarning: false,
        constants: Constants,
        adminNotes: '',
        modelValidations: {
          coordinatorId: {
            required: true
          },
          priority: {
            required: true
          },
          status: {
            required: true
          },
          dateReceived: {
            required: true
          },
          targetCompletionDate: {
            required: true
          }
        },
        newCompany: {
          builder: false,
          client: false
        },
        travelCharge: {
          state: null,
          value: null
        },
        currentFormatter : Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        })
      }
    },
    methods: {
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      showInvalidAutocompleteWarning(refAndPathName) {
        // $ref has to match prop in store
        return autocompleteTextButNoValue(this, refAndPathName, this.$store.get(`jobs/create@${refAndPathName}`))
      },
      fieldNotValidError(field) {
        return autocompleteTextButNoValueMessage(field)
      },
      validate() {
        return this.$validator.validateAll()
      },
      async findPlanNames(text, cb) {
        await jobFindPlanNames(this, text, cb, CREATE)
      },
      async setPlanNameMaybeFetchPDF(planName) {
        jobSetPlanName(this, planName, CREATE)
        await jobFetchPlanNamePDF(this, CREATE)
      },
      editPlanName() {
        jobEditPlanName(this, CREATE)
      },
      addPlanName() {
        jobAddPlanName(this, CREATE)
      },
      async findCommunities(text, cb) {
        await findCommunities(this, text, cb, 'jobs/create@community')
      },
      setCommunity(community) {
        jobSetCommunity(this, community, CREATE)

        if (this.$store.get('jobs/remember@community')) {
          this.$store.set('jobs/remembered@community', community)
        }
      },
      async findClients(text, cb) {
        await jobFindClients(this, text, cb, CREATE)
      },
      async findBuilders(text, cb) {
        await jobFindBuilders(this, text, cb, CREATE)
      },
      async findCompany(text, type) {
        return findCompany(this, text, type)
      },
      async addClientContact() {
        await jobAddClientContact(this, CREATE)
      },
      async editClientContact() {
        await jobEditClientContact(this, CREATE)
      },
      async addBuilderContact() {
        await jobAddBuilderContact(this, CREATE)
      },
      async editBuilderContact() {
        await jobEditBuilderContact(this, CREATE)
      },
      findCoordinators(text, cb) {
        jobFindCoordinators(this, text, cb, CREATE)
      },
      setCoordinator(coordinator) {
        jobSetCoordinator(this, coordinator, CREATE)
      },
      async maybeLoadClient() {
        await jobMaybeLoadClient(this, CREATE)
      },
      setCompany(company) {
        jobSetCompany(this, company)
      },
      async setClient(client) {
        this.selectedClientContact = null
        this.$store.set('jobs/create@clientContactId', null)
        this.adminNotes = _.get(client, `notesByDepartment.${this.create.departmentId}`)
        jobSetClient(this, client, CREATE)
        if (this.$store.get('jobs/remember@client')) {
          this.$store.set('jobs/remembered@client', client)
        }
        if (client.contacts.length === 1) {
          this.setClientContactMaybeRemember(client.contacts[0].id)
        }
        await jobFetchTravelCharge(this)
        if (!_.get(this.create, 'community.id')) {
          await this.maybeAddCommunityFromPreviousJob()
        }
      },
      async maybeLoadBuilder() {
        await jobMaybeLoadBuilder(this, CREATE)
      },
      setBuilder(builder) {
        this.selectedBuilderContact = null
        this.$store.set('jobs/create@builderContactId', null)
        jobSetBuilder(this, builder, CREATE)
        if (this.$store.get('jobs/remember@builder')) {
          this.$store.set('jobs/remembered@builder', builder)
        }

        if (builder.contacts.length === 1) {
          this.setBuilderContactMaybeRemember(builder.contacts[0].id)
        }

        if (!_.get(this.create, 'community.id')) {
          this.maybeAddCommunityFromPreviousJob()
        }
      },
      async fetchCommunity(id) {
        return await this.$store.dispatch('fetchById', {
          endpoint: Constants.ENTITY_ENDPOINTS.COMMUNITIES,
          id: id
        })
      },
      async maybeAddCommunityFromPreviousJob() {
        if (_.get(this.allDepartmentJobs[0], 'community.id')) {
          const response = await this.fetchCommunity(this.allDepartmentJobs[0].community.id)

          if (OK !== response.status) {
            notifyError(this, 'Failed to load Community from previous job')
            return
          }

          this.setCommunity(response.data)

          if (this.selectedCommunity.name && this.selectedCommunity.id) {
            notifySuccess(this, 'Community added from previous Job')
          }
        }
      },
      maybeAddCommunityFromPreviousJobForTemplate() {
        if (this.$store.get('jobs/create@jobTemplateId') && (this.$store.get('jobs/create@clientId') || this.$store.get('jobs/create@builderId'))) {
          this.maybeAddCommunityFromPreviousJob()
        }
      },
      verifyCoordinatorAutocompleteSelected() {
        if (!this.$store.get('jobs/create@coordinatorId')) {
          this.selectedCoordinator = null
        }
      },
      verifyClientAutocompleteSelected() {
        if (!this.newCompany.client && _.get(this, 'selectedClient.name')) {
          verifyAutocompleteSelected(this, 'jobs/create@clientId', 'client', this.selectedClient.name)
        }
      },
      verifyBuilderAutocompleteSelected() {
        if (!this.newCompany.builder && _.get(this, 'selectedBuilder.name')) {
          verifyAutocompleteSelected(this, 'jobs/create@builderId', 'builder', this.selectedBuilder.name)
        }
      },
      verifyCommunityAutocompleteSelected() {
        verifyAutocompleteSelected(this, 'jobs/create@community.id', 'community', this.selectedCommunity.name)
      },
      verifyPlanNameAutocompleteSelected() {
        verifyAutocompleteSelected(this, 'jobs/create@planNameId', 'plan name', this.selectedPlanName)
      },
      noCompanySelected(type) {
        jobNoCompanySelected(this, type)
      },
      noCommunitySelected() {
        this.selectedCommunity = { id: null, name: null }
      },
      noPlanNameSelected() {
        this.selectedPlanName = null
      },
      maybeLoadCommunity() {
        const community = this.$store.get('jobs/create@community')
        if (community) {
          this.selectedCommunity = community
        }
      },
      maybeLoadCoordinator() {
        jobMaybeLoadCoordinator(this, CREATE)
      },
      maybeResetClientBuilderCommunity() {
        if (!this.$store.get('jobs/remember@client')) {
          this.selectedClient = { id: null, name: null }
          this.clientContacts = []
        }
        if (!this.$store.get('jobs/remember@builder')) {
          this.selectedBuilder = { id: null, name: null }
          this.builderContacts = []
        }
        if (!this.$store.get('jobs/remember@community')) {
          this.selectedCommunity = { id: null, name: null }
        }
      },
      resetLocalState() {
        this.$validator.reset()
        this.$validator.pause()
        this.maybeResetClientBuilderCommunity()
        this.localDescription = ''
        this.selectedPlanName = null
        this.maybeLoadCoordinator()
        setTimeout(() => this.$validator.resume(), Constants.TIME.MILLIS.QUARTER_SECOND)
      },
      verifyBuilderSelected() {
        return jobVerifyBuilderSelected(this, CREATE)
      },
      verifyClientOrBuilderSelected() {
        return jobVerifyClientOrBuilderSelected(this, CREATE)
      },
      // called when adding a new contact on the fly
      async maybeLoadClientAndBuilder(contact) {
        await Promise.all([
          this.maybeLoadClient(),
          this.maybeLoadBuilder()
        ])
        const foundClientContact = objectBy(this.clientContacts, 'value', contact.id)
        const foundBuilderContact = objectBy(this.builderContacts, 'value', contact.id)

        this.maybeSetClientContact(foundClientContact)
        this.maybeSetBuilderContact(foundBuilderContact)

        if (this.isAddClientContact && this.remember.clientContact) {
          this.$store.set('jobs/remembered@clientContact', foundClientContact)
        }
        if (this.isAddBuilderContact && this.remember.builderContact) {
          this.$store.set('jobs/remembered@builderContact', foundBuilderContact)
        }
      },
      setClientContactMaybeRemember(clientContactOrId) {
        let clientContact = null
        if (clientContactOrId && (!clientContactOrId.value && typeof clientContactOrId === 'string')) {
          clientContact = objectBy(this.clientContacts, 'value', clientContactOrId)
        } else {
          clientContact = clientContactOrId
        }

        if (!clientContact || !this.$store.get('jobs/create@clientId')) {
          this.$store.set('jobs/remembered@clientContact', null)
          this.$store.set('jobs/create@clientContactId', null)
          this.selectedClientContact = null
          return
        }
        this.$store.set('jobs/create@clientContactId', clientContact.value)
        this.selectedClientContact = clientContact

        if (this.$store.get('jobs/remember@clientContact')) {
          this.$store.set('jobs/remembered@clientContact', clientContact)
        }
      },
      maybeSetClientContact(contact) {
        if (contact && this.isAddClientContact) {
          this.selectedClientContact = contact
          this.$store.set('jobs/create@clientContactId', contact.value)
        }
        this.isAddClientContact = false
      },
      maybeSetBuilderContact(contact) {
        if (contact && this.isAddBuilderContact) {
          this.selectedBuilderContact = contact
          this.$store.set('jobs/create@builderContactId', contact.value)
        }
        this.isAddBuilderContact = false
      },
      setBuilderContactMaybeRemember(builderContactOrId) {
        let builderContact = builderContactOrId

        if (builderContactOrId && !builderContactOrId.value && typeof builderContactOrId === 'string') {
          builderContact = objectBy(this.builderContacts, 'value', builderContactOrId)
        }

        if (!builderContact || !this.$store.get('jobs/create@builderId')) {
          this.$store.set('jobs/remembered@builderContact', null)
          this.$store.set('jobs/create@builderContactId', null)
          this.selectedBuilderContact = null
          return
        }
        this.$store.set('jobs/create@builderContactId', builderContact.value)
        this.selectedBuilderContact = builderContact

        if (this.$store.get('jobs/remember@builderContact')) {
          this.$store.set('jobs/remembered@builderContact', builderContact)
        }
      },
      async maybeResetCommunity() {
        if ((!this.create.clientId && !this.create.builderId) || !_.get(this.create, 'community.id')) {
          this.selectedCommunity = { id: null, name: null }
          return
        }

        const response = await this.fetchCommunity(_.get(this.create, 'community.id'))

        if (OK !== response.status) {
          this.selectedCommunity = { id: null, name: null }
          return
        }

        communityExistsButNotAssociated(this, response.data, this.create.clientId, this.create.builderId)
      },
      async maybeLoadRemembered() {
        const jobsRemember = this.$store.get('jobs/remember')
        const jobsRemembered = this.$store.get('jobs/remembered')

        if (jobsRemember.client && jobsRemembered.client) {
          this.$store.set('jobs/create@clientId', jobsRemembered.client.id)
          if (jobsRemember.clientContact && jobsRemembered.clientContact) {
            this.$store.set('jobs/create@clientContactId', jobsRemembered.clientContact.value)
          }
          await Promise.all([
            this.maybeLoadClient(),
            jobFetchTravelCharge(this)
          ])
        }

        if (jobsRemember.builder && jobsRemembered.builder) {
          this.$store.set('jobs/create@builderId', jobsRemembered.builder.id)
          if (this.$store.get('jobs/remember@builderContact')) {
            if (jobsRemember.builderContact && jobsRemembered.builderContact) {
              this.$store.set('jobs/create@builderContactId', jobsRemembered.builderContact.value)
            }
          }
          await this.maybeLoadBuilder()
        }

        if (jobsRemember.community && jobsRemembered.community) {
          this.$store.set('jobs/create@community', {id: jobsRemembered.community.id, name: jobsRemembered.community.name})
          this.maybeLoadCommunity()
        }

        if (jobsRemember.targetCompletionDate && jobsRemembered.targetCompletionDate) {
          this.$store.set('jobs/create@targetCompletionDate', jobsRemembered.targetCompletionDate)
        }
      },
      async generateJobNumber() {
        await jobGenerateJobNumber(this, CREATE)
      },
      async ifJobNumberExistsThenWarn() {
        await jobIfJobNumberExistsThenWarn(this)
      },
      async copyClientToBuilder() {
        await jobCopyClientToBuilder(this, CREATE)
        this.setBuilderContactMaybeRemember(this.$store.get('jobs/create@builderContactId'))
      },
      async copyBuilderToClient() {
        await jobCopyBuilderToClient(this, CREATE)
        this.setClientContactMaybeRemember(this.$store.get('jobs/create@clientContactId'))
      },
      toggleRememberedClient(val) {
        this.$store.set('jobs/remembered@client', null)

        if (this.$store.get('jobs/remember@client')) {
          if (val && this.selectedClient) {
            this.$store.set('jobs/remembered@client', this.selectedClient)
          }
        } else {
          this.$store.set('jobs/remember@clientContact', false)
          this.$store.set('jobs/remembered@clientContact', null)
        }
      },
      toggleRememberedClientContact(val) {
        if (val) {
          this.setClientContactMaybeRemember(this.$store.get('jobs/create@clientContactId'))
          return
        }
        this.$store.set('jobs/remembered@clientContact', null)
      },
      toggleRememberedBuilder(val) {
        this.$store.set('jobs/remembered@builder', null)

        if (this.$store.get('jobs/remember@builder')) {
          if (val && this.selectedBuilder) {
            this.$store.set('jobs/remembered@builder', this.selectedBuilder)
          }
        } else {
          this.$store.set('jobs/remember@builderContact', false)
          this.$store.set('jobs/remembered@builderContact', null)
        }
      },
      toggleRememberedBuilderContact(val) {
        if (val) {
          this.setBuilderContactMaybeRemember(this.$store.get('jobs/create@builderContactId'))
          return
        }
        this.$store.set('jobs/remembered@builderContact', null)
      },
      toggleRememberedCommunity(val) {
        if (val) {
          this.$store.set('jobs/remembered@community', this.selectedCommunity)
          return
        }
        this.$store.set('jobs/remembered@community', null)
      },
      toggleRememberedTargetCompletionDate(val) {
        if (val) {
          this.$store.set('jobs/remembered@targetCompletionDate', this.create.targetCompletionDate)
          return
        }
        this.$store.set('jobs/remembered@targetCompletionDate', null)
      },
      setDateReceived(dateReceived) {
        this.$store.set('jobs/create@dateReceived', dateNoon(dateReceived))
      },
      setTargetCompletionDate(targetCompletionDate) {
        this.$store.set('jobs/remembered@targetCompletionDate', targetCompletionDate)
        this.$store.set('jobs/create@targetCompletionDate', dateNoon(targetCompletionDate))
      },
      resetCompanyContactBooleans() {
        this.isAddClientContact = false
        this.isAddBuilderContact = false
      },
      handleJobTemplateSelected(jobTemplate) {
        if (jobTemplate.clientId) {
          this.maybeLoadClient()
        }
        if (jobTemplate.builderId) {
          this.maybeLoadBuilder()
        }
        if (jobTemplate.communityId) {
          this.maybeLoadCommunity()
        }
      }
    },
    beforeMount() {
      this.allDepartmentJobs = this.$store.get('jobs/byDepartmentId', this.create.departmentId)
    },
    async mounted() {
      await Promise.all([
        this.$store.cache.dispatch('users/fetchAll'),
        this.$store.cache.dispatch('statuses/fetchByEntityType', 'Job'),
        this.$store.cache.dispatch('priorities/fetchByEntityType', 'Job'),
        this.maybeLoadRemembered()
      ])
      this.$store.set('jobs/create@coordinatorId', this.$store.get('security/userId'))
      this.maybeLoadCoordinator()

      this.$eventHub.$on('companySaved', this.setCompany)
      this.$eventHub.$on('noCompanySelected', this.noCompanySelected)
      this.$eventHub.$on('communitySaved', this.setCommunity)
      this.$eventHub.$on('noCommunitySelected', this.noCommunitySelected)
      this.$eventHub.$on('planNameSaved', this.setPlanNameMaybeFetchPDF)
      this.$eventHub.$on('noPlanNameSelected', this.noPlanNameSelected)
      this.$eventHub.$on('companyContactUpdated', this.maybeLoadClientAndBuilder)
      this.$eventHub.$on('noCompanyContactSelected', this.resetCompanyContactBooleans)
      this.$eventHub.$on('jobTemplateSelected', this.handleJobTemplateSelected)
      this.$eventHub.$on('jobTemplateDeselected', this.maybeResetClientBuilderCommunity)
      this.$eventHub.$on('navigateBackJobWizard', this.resetLocalState)
      this.$eventHub.$on('navigateForwardJobWizard', this.maybeAddCommunityFromPreviousJobForTemplate)
    },
    beforeDestroy() {
      this.$eventHub.$off('companySaved')
      this.$eventHub.$off('noCompanySelected')
      this.$eventHub.$off('communitySaved')
      this.$eventHub.$off('noCommunitySelected')
      this.$eventHub.$off('planNameSaved')
      this.$eventHub.$off('noPlanNameSelected')
      this.$eventHub.$off('companyContactUpdated')
      this.$eventHub.$off('noCompanyContactSelected')
      this.$eventHub.$off('jobTemplateSelected')
      this.$eventHub.$off('jobTemplateDeselected')
      this.$eventHub.$off('navigateBackJobWizard')
      this.$eventHub.$off('navigateForwardJobWizard')
    },
    destroyed() {
      this.resetLocalState()
      // DO NOT call 'jobs/resetCreate' here - breaks Add Job
    },
    watch: {
      selectedPlanName: {
        handler: function (selectedPlanName) {
          if (!selectedPlanName) {
            this.$store.commit('planNames/results', [])
            this.$store.set('jobs/create@planNameId', null)
            this.planNamePDF = null
          }
        }
      },
      'selectedBuilder.name': {
        handler: function (selectedBuilderName) {
          if (!selectedBuilderName) {
            this.isBuilderResult = false
            this.$store.commit('companies/autocompleteResultsBuilder', [])
            this.$store.set('jobs/create@builderId', null)
            this.$store.set('jobs/create@builderContactId', null)
            this.$store.set('jobs/remembered@builder', null)
            this.$store.set('jobs/remembered@builderContact', null)
            this.selectedBuilder = { id: null, name: null }
            this.selectedBuilderContact = null
            this.builderContacts = []
            this.maybeResetCommunity()
          }
          if (this.$store.get('jobs/create@builderContactId')) {
            this.setBuilderContactMaybeRemember(this.$store.get('jobs/create@builderContactId'))
          }
        }
      },
      'selectedClient.name': {
        handler: function (selectedClientName) {
          if (!selectedClientName) {
            this.isClientResult = false
            this.$store.commit('companies/autocompleteResultsClient', [])
            this.$store.set('jobs/create@clientId', null)
            this.$store.set('jobs/create@clientContactId', null)
            this.$store.set('jobs/remembered@client', null)
            this.$store.set('jobs/remembered@clientContact', null)
            this.selectedClient = { id: null, name: null }
            this.selectedClientContact = null
            this.clientContacts = []
            this.$validator.errors.remove('clientId')
            this.travelCharge = { state: null, value: null }
            this.adminNotes = '' //reinitialize adminNotes if client unselected from "Billable Client" input field
            this.maybeResetCommunity()
          }
          if (this.$store.get('jobs/create@clientContactId')) {
            this.setClientContactMaybeRemember(this.$store.get('jobs/create@clientContactId'))
          }
        }
      },
      selectedCoordinator: {
        handler: function (selectedCoordinator) {
          if (!selectedCoordinator) {
            this.$store.set('jobs/create@coordinatorId', null)
          }
        }
      },
      'selectedCommunity.name': {
        handler: function (selectedCommunityName) {
          if (!selectedCommunityName) {
            this.$store.set('jobs/create@community', {})
            this.$store.set('jobs/remembered@community', null)
            this.selectedCommunity = { id: null, name: null }
          }
        }
      }
    }
  }
</script>

<style scoped>
  .add-job >>> #autocomplete-client .v-autocomplete-list, #autocomplete-builder .v-autocomplete-list {
    min-width: 280px;
    margin-top: 12px;
  }
  .add-job >>> .job-name-container {
    background-color: lightgrey;
    padding: 11px 0 4px 8px;
    border-radius: 5px;
  }
  .add-job >>> .tiny-label-button {
    padding: 1px 5px;
    margin-top: -1px;
    float: right;
  }
</style>
